/**
 * DesignPanel — properties panel for the HTML editor's design mode.
 *
 * Docked to the right of the preview when an element is selected. Inputs
 * initialize from the element's COMPUTED styles (captured by the inspector
 * at select time) and every change calls `onStyle(prop, cssValue)` — the
 * editor applies it optimistically in the iframe and debounces the
 * deterministic source write-back (see HtmlEditor.handleDesignStyle).
 *
 * Mount with `key={selection.locAttr}` so switching elements (or a reload
 * re-tagging the source) resets the inputs to the fresh computed values.
 *
 * V1 property set (v0/Replit parity): typography (size/weight/align/color),
 * background, per-side padding/margin, border radius. Everything writes to
 * the element's inline `style` attribute — deterministic and always safe;
 * "do it properly in the stylesheet" is an AI-path ask.
 *
 * Phase 4a adds a Size section (width/height with a px/%/rem/auto unit
 * picker) seeded from the AUTHORED value the inspector detected — computed
 * styles only report used pixels, which would misrepresent `50%` and
 * auto-sized elements alike. Unit switches convert the CURRENT rendered
 * size into the new unit (the box doesn't jump); `auto` removes the inline
 * declaration (a stylesheet size, if any, then wins again).
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { RgbaColorPicker } from 'react-colorful'
import { AlignLeft, AlignCenter, AlignRight, X, Check, Pencil, Plus, Bold, Italic, Underline, Strikethrough } from 'lucide-react'
import type { DesignElementSelection, DesignSizing, DesignLayout } from './HtmlViewer.js'

interface DesignPanelProps {
  selection: DesignElementSelection
  onStyle: (prop: string, value: string) => void
  /** Commit several declarations as ONE atomic source edit (the paste-CSS
   *  pencil). Optional — falls back to per-prop onStyle calls. */
  onStyleBatch?: (decls: Array<{ prop: string; value: string }>) => void
  /** Parent-held scroll memory. The panel remounts on every payload (new
   *  `seq`) to reseed its inputs, which would reset the scroll position to
   *  the top on every menu/drag edit. This survives the remount: we restore
   *  it when the SAME element is re-seeded and reset to top when a different
   *  element (`locAttr`) is selected. */
  scrollStore?: React.MutableRefObject<{ loc: string; top: number }>
}

function pxNum(v: string | undefined): number {
  const n = parseFloat(v || '')
  return Number.isFinite(n) ? Math.round(n) : 0
}

function Section({ title, action, children }: { title: string; action?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="relative px-2.5 py-2 border-b themed-border-secondary">
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-[9px] font-semibold uppercase tracking-wider themed-text-muted">{title}</div>
        {action}
      </div>
      {children}
    </div>
  )
}

/** Scrubbable number — the panel's standard numeric control. Drag to
 *  adjust (Shift = ×10), double-click to type; Enter/blur closes the
 *  editor. Empty value = unset: shows the placeholder muted, and a drag
 *  starts from `scrubBase` so an auto field can be dragged straight into
 *  a value. Internal state re-syncs when the seed prop changes (unit
 *  switches, panel refresh). */
function NumScrub({ value, placeholder = '0', scrubBase, step = 1, min, onCommit }: {
  value: string
  placeholder?: string
  scrubBase?: number
  step?: number
  min?: number
  onCommit: (raw: string) => void
}) {
  const [v, setV] = useState(value)
  const [editing, setEditing] = useState(false)
  const lastSeed = useRef(value)
  if (lastSeed.current !== value) {
    lastSeed.current = value
    if (v !== value) setV(value)
  }
  const drag = useRef<{ x: number; n: number; moved: boolean } | null>(null)
  const fmt = (n: number) => String(Math.round(n * 100) / 100)
  const clamp = (n: number) => (min !== undefined ? Math.max(min, n) : n)

  if (editing) {
    return (
      <input
        type="number"
        autoFocus
        className="themed-input rounded px-1 py-0.5 text-[11px] w-full text-center"
        value={v}
        onChange={(e) => {
          setV(e.target.value)
          const n = parseFloat(e.target.value)
          if (Number.isFinite(n)) onCommit(fmt(clamp(n)))
          else if (e.target.value === '') onCommit('')
        }}
        onBlur={() => setEditing(false)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'Escape') setEditing(false) }}
      />
    )
  }
  return (
    <span
      className={`block w-full text-center font-mono text-[11px] px-1 py-0.5 rounded cursor-ew-resize select-none border border-transparent hover:themed-border-secondary ${v ? 'themed-text underline decoration-dotted decoration-1 underline-offset-2' : 'themed-text-muted'}`}
      title="Drag to adjust · Shift = ×10 · double-click to type"
      onPointerDown={(e) => {
        const start = v ? parseFloat(v) : (scrubBase ?? 0)
        if (!Number.isFinite(start)) return
        drag.current = { x: e.clientX, n: start, moved: false }
        e.currentTarget.setPointerCapture(e.pointerId)
        e.preventDefault()
      }}
      onPointerMove={(e) => {
        const d = drag.current
        if (!d) return
        const dx = e.clientX - d.x
        if (!d.moved && Math.abs(dx) < 3) return
        d.moved = true
        const raw = fmt(clamp(d.n + Math.round(dx / 3) * step * (e.shiftKey ? 10 : 1)))
        setV(raw)
        onCommit(raw)
      }}
      onPointerUp={() => { drag.current = null }}
      onDoubleClick={() => setEditing(true)}
    >
      {v || placeholder}
    </span>
  )
}

function PxInput({ label, initial, onCommit }: { label: string; initial: number; onCommit: (n: number) => void }) {
  return (
    <label className="flex flex-col gap-0.5 min-w-0">
      <span className="text-[9px] themed-text-muted text-center">{label}</span>
      <NumScrub
        value={String(initial)}
        onCommit={(raw) => {
          const n = parseFloat(raw)
          if (Number.isFinite(n)) onCommit(n)
        }}
      />
    </label>
  )
}

function SidesGrid({ prefix, resolve, onStyle }: {
  prefix: 'padding' | 'margin'
  /** Effective value for a longhand (live decl → shorthand expansion →
   *  computed) — the shared source of truth resolver. */
  resolve: (prop: string, computedKey: string) => string
  onStyle: (prop: string, value: string) => void
}) {
  const sides = ['Top', 'Right', 'Bottom', 'Left'] as const
  return (
    <div className="grid grid-cols-4 gap-1">
      {sides.map((side) => (
        <PxInput
          key={side}
          label={side[0]}
          initial={pxNum(resolve(`${prefix}-${side.toLowerCase()}`, `${prefix}${side}`))}
          onCommit={(n) => onStyle(`${prefix}-${side.toLowerCase()}`, `${n}px`)}
        />
      ))}
    </div>
  )
}

/** One Size axis: a small label + a scrubbable string value — the exact
 *  line-height control. Drag the number to adjust (keeps its unit, %/rem/
 *  em/calc all survive), double-click to type anything; dragging an unset
 *  field starts from the element's rendered px. No unit dropdown — the
 *  unit is just part of the value string. */
function SizeField({ label, prop, value, placeholder, computedPx, onCommit }: {
  label: string
  prop: string
  value: string
  placeholder: string
  computedPx: number
  onCommit: (value: string) => void
}) {
  return (
    <div className="flex items-center gap-1 min-w-0">
      <span className="text-[9px] themed-text-muted w-[4.5rem] shrink-0">{label}</span>
      <ValueEditor prop={prop} value={value} placeholder={placeholder} scrubSeed={computedPx} onCommit={onCommit} />
    </div>
  )
}

function SizeSection({ sizing, width, height, onStyle }: {
  sizing: DesignSizing
  width: number
  height: number
  onStyle: (prop: string, value: string) => void
}) {
  // Grouped by axis; unset shows the placeholder (auto / none), not the
  // computed px — computed would read as "already set".
  const axes = [
    { label: 'W', authored: sizing.width, prop: 'width', px: width, unset: 'auto' },
    { label: 'Min W', authored: sizing.minWidth, prop: 'min-width', px: width, unset: 'none' },
    { label: 'Max W', authored: sizing.maxWidth, prop: 'max-width', px: width, unset: 'none' },
    { label: 'H', authored: sizing.height, prop: 'height', px: height, unset: 'auto' },
    { label: 'Min H', authored: sizing.minHeight, prop: 'min-height', px: height, unset: 'none' },
    { label: 'Max H', authored: sizing.maxHeight, prop: 'max-height', px: height, unset: 'none' },
  ]
  return (
    <Section title="Size">
      <div className="flex flex-col gap-1">
        {axes.map((a) => (
          <SizeField
            key={a.prop}
            label={a.label}
            prop={a.prop}
            value={a.authored.value}
            placeholder={a.unset}
            computedPx={a.px}
            onCommit={(val) => onStyle(a.prop, val)}
          />
        ))}
      </div>
    </Section>
  )
}

// ─── Layout section (USS-inspector style) ──────────────────────────────
// Container controls appear when the element itself is a flex container;
// item controls (grow/shrink/basis/align-self) when its PARENT is one —
// context-sensitivity is what keeps the panel honest: a flex-grow input on
// a block-flow child would be a lie.

const DISPLAYS = ['block', 'flex', 'inline-block', 'inline-flex', 'inline', 'grid', 'none']
const DIRECTIONS = ['row', 'row-reverse', 'column', 'column-reverse']
const WRAPS = ['nowrap', 'wrap', 'wrap-reverse']
const JUSTIFIES = ['flex-start', 'center', 'flex-end', 'space-between', 'space-around', 'space-evenly']
const ALIGN_ITEMS = ['stretch', 'flex-start', 'center', 'flex-end', 'baseline']
const ALIGN_SELF = ['auto', 'stretch', 'flex-start', 'center', 'flex-end', 'baseline']
const ALIGN_CONTENT = ['normal', 'flex-start', 'center', 'flex-end', 'space-between', 'space-around', 'stretch']
const VISIBILITIES = ['visible', 'hidden', 'collapse']
const VERTICAL_ALIGNS = ['baseline', 'top', 'middle', 'bottom', 'text-top', 'text-bottom', 'sub', 'super']
const OVERFLOWS = ['visible', 'hidden', 'auto', 'scroll', 'clip']
const CURSORS = ['auto', 'default', 'pointer', 'grab', 'grabbing', 'text', 'move', 'not-allowed', 'wait', 'crosshair', 'help', 'none']
const POINTER_EVENTS = ['auto', 'none']
// vertical-align only affects inline-level boxes and table cells; on a
// block it's a no-op, so the control is hidden there (use flex align-items
// to vertically align block content — that's the honest modern way).
const VALIGN_DISPLAYS = new Set(['inline', 'inline-block', 'inline-flex', 'inline-table', 'table-cell'])

function LayoutSelect({ label, initial, options, onCommit }: {
  label: string
  initial: string
  options: string[]
  onCommit: (v: string) => void
}) {
  const [v, setV] = useState(initial)
  // An off-list computed value (e.g. display:table) joins as an extra
  // option so the seed displays honestly.
  const opts = options.includes(v) ? options : [v, ...options]
  return (
    <label className="flex flex-col gap-0.5 flex-1 min-w-0">
      <span className="text-[9px] themed-text-muted">{label}</span>
      <select
        className="themed-input rounded px-1 py-0.5 text-[10px] w-full"
        value={v}
        onChange={(e) => { setV(e.target.value); onCommit(e.target.value) }}
      >
        {opts.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  )
}

/** Unitless number (flex-grow / flex-shrink). */
function NumInput({ label, initial, onCommit }: { label: string; initial: string; onCommit: (v: string) => void }) {
  return (
    <label className="flex flex-col gap-0.5 flex-1 min-w-0">
      <span className="text-[9px] themed-text-muted text-center">{label}</span>
      <NumScrub
        value={initial}
        min={0}
        onCommit={(raw) => { if (raw !== '') onCommit(raw) }}
      />
    </label>
  )
}

/** flex-basis: free-form but commit only when it's a valid basis value. */
function BasisInput({ initial, onCommit }: { initial: string; onCommit: (v: string) => void }) {
  const [v, setV] = useState(initial)
  return (
    <label className="flex flex-col gap-0.5 flex-1 min-w-0">
      <span className="text-[9px] themed-text-muted text-center">Basis</span>
      <input
        type="text"
        className="themed-input rounded px-1 py-0.5 text-[11px] w-full text-center"
        value={v}
        placeholder="auto"
        onChange={(e) => {
          setV(e.target.value)
          const t = e.target.value.trim()
          if (/^(auto|-?[0-9.]+(px|%|rem|em|vw|vh))$/.test(t)) onCommit(t)
        }}
      />
    </label>
  )
}

const POSITIONS = ['static', 'relative', 'absolute', 'fixed', 'sticky']

/** Inset / z-index input. Shows the AUTHORED value: a number when the
 *  inline value is a length, or blank (placeholder `auto`) when the inline
 *  value is `auto` — the computed px would resolve `auto` to a number and
 *  hide that an edge isn't pinned. When nothing is set inline, the computed
 *  px is the placeholder so a positioned element still shows where it sits.
 *  Scrub or type to set px; clearing sets `auto`. */
function InsetInput({ label, authored, computed, unitless = false, onCommit }: {
  label: string
  /** Inline value: 'auto' | '<len>' | undefined (not set inline). */
  authored: string | undefined
  /** Computed px (used value) — placeholder when not authored. */
  computed: string
  unitless?: boolean
  onCommit: (v: string) => void
}) {
  const isAuto = authored === 'auto'
  const m = (authored ?? '').match(/^(-?[\d.]+)/)
  const seed = !isAuto && m ? String(Math.round(parseFloat(m[1]))) : ''
  const compN = parseFloat(computed)
  const placeholder = isAuto || authored === undefined
    ? (isAuto ? 'auto' : (Number.isFinite(compN) ? String(Math.round(compN)) : 'auto'))
    : 'auto'
  return (
    <label className="flex flex-col gap-0.5 flex-1 min-w-0">
      <span className="text-[9px] themed-text-muted text-center">{label}</span>
      <NumScrub
        value={seed}
        placeholder={placeholder}
        onCommit={(raw) => {
          if (raw === '') onCommit('auto') // cleared = explicitly auto
          else onCommit(unitless ? raw : `${raw}px`)
        }}
      />
    </label>
  )
}

function PositionSection({ layout, authored, onStyle }: { layout: DesignLayout; authored: Record<string, string>; onStyle: (prop: string, value: string) => void }) {
  const [position, setPosition] = useState(layout.position || 'static')
  const parentFlex = layout.parentDisplay === 'flex' || layout.parentDisplay === 'inline-flex'
  return (
    <Section title="Position">
      <div className="flex flex-col gap-1.5">
        <LayoutSelect label="Position" initial={position} options={POSITIONS} onCommit={(v) => { setPosition(v); onStyle('position', v) }} />
        {position !== 'static' && (
          <>
            <div className="flex gap-1">
              <InsetInput label="T" authored={authored['top']} computed={layout.top} onCommit={(v) => onStyle('top', v)} />
              <InsetInput label="R" authored={authored['right']} computed={layout.right} onCommit={(v) => onStyle('right', v)} />
              <InsetInput label="B" authored={authored['bottom']} computed={layout.bottom} onCommit={(v) => onStyle('bottom', v)} />
              <InsetInput label="L" authored={authored['left']} computed={layout.left} onCommit={(v) => onStyle('left', v)} />
            </div>
            <div className="flex gap-1">
              <div className="w-1/4 pr-0.5">
                <InsetInput label="Z" authored={authored['z-index']} computed={layout.zIndex} unitless onCommit={(v) => onStyle('z-index', v)} />
              </div>
            </div>
          </>
        )}
        {/* Self-alignment (how this element aligns itself in a flex/grid
            parent) — only meaningful when the parent is a flex container. */}
        {parentFlex && (
          <LayoutSelect label="Align self" initial={layout.alignSelf} options={ALIGN_SELF} onCommit={(v) => onStyle('align-self', v)} />
        )}
      </div>
    </Section>
  )
}

function LayoutSection({ layout, onStyle }: { layout: DesignLayout; onStyle: (prop: string, value: string) => void }) {
  const [display, setDisplay] = useState(layout.display)
  const isFlex = display === 'flex' || display === 'inline-flex'
  const parentFlex = layout.parentDisplay === 'flex' || layout.parentDisplay === 'inline-flex'
  // computed 'normal' means the initial value — map to the real keyword
  const justify = layout.justifyContent === 'normal' ? 'flex-start' : layout.justifyContent
  const alignI = layout.alignItems === 'normal' ? 'stretch' : layout.alignItems
  const [visibility, setVisibility] = useState(layout.visibility || 'visible')
  return (
    <Section title="Layout">
      <div className="flex flex-col gap-1.5">
        <div className="flex gap-1">
          <LayoutSelect label="Display" initial={display} options={DISPLAYS} onCommit={(v) => { setDisplay(v); onStyle('display', v) }} />
          <LayoutSelect label="Visible" initial={visibility} options={VISIBILITIES} onCommit={(v) => { setVisibility(v); onStyle('visibility', v) }} />
        </div>
        {isFlex && (
          <>
            <div className="flex gap-1">
              <LayoutSelect label="Direction" initial={layout.flexDirection} options={DIRECTIONS} onCommit={(v) => onStyle('flex-direction', v)} />
              <LayoutSelect label="Wrap" initial={layout.flexWrap} options={WRAPS} onCommit={(v) => onStyle('flex-wrap', v)} />
            </div>
            <div className="flex gap-1">
              <LayoutSelect label="Justify" initial={justify} options={JUSTIFIES} onCommit={(v) => onStyle('justify-content', v)} />
              <LayoutSelect label="Align" initial={alignI} options={ALIGN_ITEMS} onCommit={(v) => onStyle('align-items', v)} />
            </div>
            <div className="flex gap-1">
              {/* Content alignment across flex LINES — only bites when the
                  container wraps; still safe to set otherwise. */}
              <LayoutSelect label="Align content" initial={layout.alignContent === 'normal' ? 'normal' : layout.alignContent} options={ALIGN_CONTENT} onCommit={(v) => onStyle('align-content', v)} />
              <div className="w-16 shrink-0">
                <PxInput label="Gap" initial={pxNum(layout.gap)} onCommit={(n) => onStyle('gap', `${n}px`)} />
              </div>
            </div>
          </>
        )}
        {parentFlex && (
          <>
            <div className="text-[9px] font-semibold uppercase tracking-wider themed-text-muted mt-0.5">
              Flex item · in {layout.parentFlexDirection || 'row'}
            </div>
            <div className="flex gap-1">
              <NumInput label="Grow" initial={layout.flexGrow} onCommit={(v) => onStyle('flex-grow', v)} />
              <NumInput label="Shrink" initial={layout.flexShrink} onCommit={(v) => onStyle('flex-shrink', v)} />
              <BasisInput initial={layout.flexBasis} onCommit={(v) => onStyle('flex-basis', v)} />
            </div>
          </>
        )}
        <div className="flex gap-1">
          <LayoutSelect label="Overflow" initial={layout.overflow || 'visible'} options={OVERFLOWS} onCommit={(v) => onStyle('overflow', v)} />
          <div className="flex-1 flex items-center gap-1 min-w-0">
            <span className="text-[9px] themed-text-muted shrink-0">Opacity</span>
            <ValueEditor prop="opacity" value={layout.opacity === '1' ? '' : layout.opacity} placeholder="1" onCommit={(v) => onStyle('opacity', v)} />
          </div>
        </div>
      </div>
    </Section>
  )
}

// ─── "More styles" — DevTools-style rows for the common properties ─────
// The value string is TOKENIZED once, and every affordance hangs off a
// token: numbers scrub individually (drag; Shift = ×10), colors get a
// picker swatch in front of the token, keyword-driven properties render a
// dropdown. Truncated values grow a hover OVERLAY — the same token view
// with room, not an input. Double-click (row or overlay) enters raw
// string editing, with per-property suggestions. Everything validates
// through the browser's own engine (CSS.supports) before committing —
// invalid shows red, never reaches the source.

/** Rows that stay in "More styles" — everything section-owned (background,
 *  border, text, spacing, size, layout) lives in its section instead. */
// Anything the panel doesn't give a dedicated control — plus whatever the
// user pastes / sets that isn't section-owned — surfaces here.
const MORE_ROWS: Array<{ prop: string; key: string; suggest: string[]; enums?: string[] }> = []

/** Inline decls that already have a structured section — kept out of the
 *  "More styles" extra rows so the panel never shows a property twice. */
const EXTRA_ROW_EXCLUDE = /^(width|height|min-width|min-height|max-width|max-height|box-sizing|padding|padding-.*|margin|margin-.*|font-family|font-size|font-weight|font-style|text-align|text-wrap|text-decoration|text-decoration-.*|color|background|background-.*|border|border-.*|outline|outline-.*|-webkit-.*|line-height|letter-spacing|word-spacing|text-overflow|text-shadow|vertical-align|box-shadow|flex|flex-.*|align-self|align-content|position|top|right|bottom|left|inset|z-index|display|gap|justify-content|align-items|opacity|visibility|overflow|overflow-x|overflow-y|cursor|pointer-events|transform)$/

function parseInlineDecls(s: string): Record<string, string> {
  const out: Record<string, string> = {}
  for (const part of s.split(';')) {
    const i = part.indexOf(':')
    if (i < 0) continue
    const p = part.slice(0, i).trim().toLowerCase()
    const v = part.slice(i + 1).trim()
    if (p) out[p] = v
  }
  return out
}

/** Authored top/right/bottom/left, EXPANDING the `inset` shorthand. When we
 *  set all four insets inline, the browser SERIALIZES them back as
 *  `inset: <t> <r> <b> <l>` — so `getAttribute('style')` (and thus the
 *  payload) has no per-side keys and the panel loses `auto`. Expand `inset`
 *  (via the browser's own parser) so each side is visible again. */
function authoredInsets(inlineStyle: string): Record<string, string> {
  const map = parseInlineDecls(inlineStyle)
  if (map.inset !== undefined) {
    for (const lh of ['top', 'right', 'bottom', 'left']) {
      if (map[lh] === undefined) {
        const v = expandShorthand('inset', map.inset, lh)
        if (v) map[lh] = v
      }
    }
  }
  return map
}

/** Longhand → the shorthand that can set it. Used by the panel's value
 *  resolution: an explicit longhand wins, else a set shorthand is EXPANDED
 *  (browser probe below), else the computed value shows muted. */
const SHORTHAND_OF: Record<string, string> = {
  'background-color': 'background',
  'background-image': 'background',
  'background-clip': 'background',
  'background-size': 'background',
  'background-position': 'background',
  'background-repeat': 'background',
  'background-origin': 'background',
  'background-attachment': 'background',
  'border-width': 'border',
  'border-style': 'border',
  'border-color': 'border',
  'padding-top': 'padding',
  'padding-right': 'padding',
  'padding-bottom': 'padding',
  'padding-left': 'padding',
  'margin-top': 'margin',
  'margin-right': 'margin',
  'margin-bottom': 'margin',
  'margin-left': 'margin',
}

/** The longhands each shorthand governs — the compose/decompose set. The
 *  panel stores ONLY longhands (never the shorthand itself), so the rows
 *  below are the single source of truth and their remove buttons act on
 *  real declarations. This is the FULL standard reset scope of each
 *  shorthand, so entering a shorthand is decoded honestly (exactly what the
 *  browser does) — `background: red` really does reset `background-clip`
 *  etc. `border` omits `border-radius` (the CSS shorthand doesn't touch
 *  radius either); vendor-prefixed props (`-webkit-background-clip`) aren't
 *  part of any standard shorthand, so they're never reset by one. */
const SHORTHAND_LONGHANDS: Record<string, string[]> = {
  background: ['background-image', 'background-position', 'background-size', 'background-repeat', 'background-origin', 'background-clip', 'background-attachment', 'background-color'],
  border: ['border-width', 'border-style', 'border-color'],
  padding: ['padding-top', 'padding-right', 'padding-bottom', 'padding-left'],
  margin: ['margin-top', 'margin-right', 'margin-bottom', 'margin-left'],
}

// Shared detached probe — the browser's own parser does all shorthand
// math, so there are no CSS grammar tables to maintain and results can
// never disagree with the engine.
let styleProbe: CSSStyleDeclaration | null = null
function probe(): CSSStyleDeclaration | null {
  if (typeof document === 'undefined') return null
  if (!styleProbe) styleProbe = document.createElement('div').style
  styleProbe.cssText = ''
  return styleProbe
}

/** Expand a stored shorthand to one longhand — effective() display
 *  fallback for a pasted shorthand that wasn't decomposed. */
function expandShorthand(shorthand: string, value: string, longhand: string): string {
  const p = probe()
  if (!p) return ''
  try { p.setProperty(shorthand, value) } catch { return '' }
  return p.getPropertyValue(longhand)
}

// A longhand's INITIAL (default) value, read once from a real computed
// style — used by the pencil-seed composer. Setting a shorthand to the
// `initial` KEYWORD and reading longhands back is unreliable (browsers
// return '' or 'initial', not the concrete value).
let initialCache: Record<string, string> | null = null
function initialValue(longhand: string): string {
  if (initialCache) return initialCache[longhand] ?? ''
  initialCache = {}
  if (typeof document === 'undefined' || !document.body) return ''
  const el = document.createElement('div')
  el.style.cssText = 'position:absolute;left:-99999px;top:-99999px;visibility:hidden'
  document.body.appendChild(el)
  const cs = getComputedStyle(el)
  for (const lhs of Object.values(SHORTHAND_LONGHANDS)) {
    for (const lh of lhs) initialCache[lh] = cs.getPropertyValue(lh)
  }
  el.remove()
  return initialCache[longhand] ?? ''
}

/** Break a shorthand into per-longhand commits: each governed longhand
 *  gets the value the shorthand produces, or '' (clear) when that value is
 *  the shorthand's INITIAL — so `background: red` sets only the color and
 *  clears leftover defaults, without polluting the source with
 *  `background-repeat: repeat` noise rows. */
function decomposeShorthand(shorthand: string, value: string): Array<{ prop: string; value: string }> {
  const longhands = SHORTHAND_LONGHANDS[shorthand]
  if (!longhands) return [{ prop: shorthand, value }]
  // Positional shorthands (padding/margin): keep every side in AUTHORED
  // form (`0` is real; units must not be normalized to px). Inline probe.
  if (shorthand === 'padding' || shorthand === 'margin') {
    const p = probe()
    if (!p) return [{ prop: shorthand, value }]
    try { p.setProperty(shorthand, value) } catch { return [] }
    return longhands.map((lh) => ({ prop: lh, value: p.getPropertyValue(lh) }))
  }
  // Keyword shorthands (background/border): diff the produced longhands
  // against the defaults THROUGH THE SAME getComputedStyle path, so the
  // serializations match (inline `0% 0%` vs computed `0px 0px` was keeping
  // every default as a noise row and overwriting size/repeat/etc.). Values
  // land computed-normalized (colors → rgb), which the rows already show.
  if (typeof document === 'undefined' || !document.body) return [{ prop: shorthand, value }]
  const el = document.createElement('div')
  el.style.cssText = 'position:absolute;left:-99999px;top:-99999px;visibility:hidden'
  document.body.appendChild(el)
  const cs = getComputedStyle(el)
  const initials = longhands.map((lh) => cs.getPropertyValue(lh))
  el.style.setProperty(shorthand, value)
  const out = longhands.map((lh, i) => {
    const got = cs.getPropertyValue(lh)
    return { prop: lh, value: got && got !== initials[i] ? got : '' }
  })
  el.remove()
  return out
}

/** Regenerate the shorthand from the current EFFECTIVE longhands — seeds
 *  the pencil each time it opens. `resolve` gives each longhand's effective
 *  value (explicit inline → else computed), so a background that comes from
 *  a STYLESHEET (not an inline declaration) still seeds instead of coming
 *  back empty. Keyword shorthands skip longhands left at their initial
 *  (default) so there's no `repeat scroll 0% 0%` noise; positional
 *  shorthands (padding/margin) keep all four sides in order. */
function composeShorthand(shorthand: string, live: Record<string, string>, resolve: (lh: string) => string): string {
  if (live[shorthand]) return live[shorthand] // a pasted, undecomposed shorthand
  const longhands = SHORTHAND_LONGHANDS[shorthand]
  if (!longhands) return ''
  if (shorthand === 'padding' || shorthand === 'margin') {
    const [t, r, b, l] = longhands.map(resolve)
    return t && r && b && l ? collapse4(t, r, b, l) : ''
  }
  const set: Record<string, string> = {}
  for (const lh of longhands) {
    const v = resolve(lh)
    if (v && v !== initialValue(lh)) set[lh] = v
  }
  if (shorthand === 'background') return joinBackground(set)
  return longhands.filter((lh) => set[lh]).map((lh) => set[lh]).join(' ')
}

/** Assemble a VALID `background` shorthand from the set longhands: position
 *  and size need the `pos / size` slash, and `background-clip: text` is
 *  omitted (it can't be expressed in the shorthand — it stays in its own
 *  Clip row). The order follows the shorthand grammar. */
function joinBackground(set: Record<string, string>): string {
  const parts: string[] = []
  if (set['background-image']) parts.push(set['background-image'])
  const pos = set['background-position']
  const size = set['background-size']
  if (pos && size) parts.push(`${pos} / ${size}`)
  else if (pos) parts.push(pos)
  else if (size) parts.push(`0% 0% / ${size}`)
  if (set['background-repeat']) parts.push(set['background-repeat'])
  if (set['background-origin']) parts.push(set['background-origin'])
  const clip = set['background-clip']
  if (clip && clip !== 'text') parts.push(clip)
  if (set['background-attachment']) parts.push(set['background-attachment'])
  if (set['background-color']) parts.push(set['background-color'])
  return parts.join(' ')
}

/** The pencil's editor — an absolute popover anchored under the section
 *  header, with explicit ✓ apply / ✗ cancel. Enter or ✓ applies (decomposes
 *  into the rows below); Escape, ✗, or a click OUTSIDE cancels — nothing is
 *  committed unless the user confirms. Re-seeded from the live longhands on
 *  every open, so it always reflects the current rows. */
function ShorthandPopover({ prop, seed, onApply, onClose }: {
  prop: string
  seed: string
  onApply: (v: string) => void
  onClose: () => void
}) {
  const [v, setV] = useState(seed)
  const [invalid, setInvalid] = useState(false)
  const done = useRef(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const apply = () => {
    if (done.current) return
    const t = v.trim()
    if (t !== '' && typeof CSS !== 'undefined' && CSS.supports && !CSS.supports(prop, t)) { setInvalid(true); return }
    done.current = true
    if (t !== seed.trim()) onApply(t)
    onClose()
  }
  const cancel = () => {
    if (done.current) return
    done.current = true
    onClose()
  }
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const el = e.target as HTMLElement
      if (rootRef.current?.contains(el)) return
      if (el.closest?.(`[data-pencil="${prop}"]`)) return // its own pencil toggles it
      cancel() // clicking outside discards
    }
    document.addEventListener('mousedown', onDown, true)
    // Cleanup ONLY detaches the listener — it must not call cancel()/onClose
    // (StrictMode's setup→cleanup→setup double-invoke would close the popover
    // the instant it opens). Cancel is driven by explicit user actions
    // (outside click, ✗, Escape); an unmount from a section switch simply
    // discards, which is the same as cancel.
    return () => document.removeEventListener('mousedown', onDown, true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  return (
    <div
      ref={rootRef}
      className="absolute left-2 right-2 top-7 z-30 themed-bg-tertiary border themed-border rounded-md shadow-xl p-1.5"
    >
      <div className="flex items-center gap-1">
        <input
          type="text"
          autoFocus
          className={`themed-input rounded flex-1 min-w-0 px-1 py-0.5 text-[10px] font-mono ${invalid ? 'text-red-400' : ''}`}
          value={v}
          placeholder={prop}
          onChange={(e) => { setV(e.target.value); setInvalid(false) }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') apply()
            else if (e.key === 'Escape') cancel()
          }}
        />
        <button type="button" data-tooltip="Apply" aria-label="Apply" className="shrink-0 text-emerald-400 hover:text-emerald-300 cursor-pointer" onClick={apply}>
          <Check size={13} />
        </button>
        <button type="button" data-tooltip="Cancel" aria-label="Cancel" className="shrink-0 themed-text-muted hover:themed-text cursor-pointer" onClick={cancel}>
          <X size={13} />
        </button>
      </div>
    </div>
  )
}

// ── Value tokenizer ──
type ValueTok =
  | { t: 'num'; raw: string; num: number; unit: string }
  | { t: 'color'; raw: string }
  | { t: 'txt'; raw: string }

const TOKEN_RE = /#[0-9a-fA-F]{3,8}\b|rgba?\([^)]*\)|hsla?\([^)]*\)|-?\d*\.?\d+[a-z%]*/g

function tokenizeValue(v: string): ValueTok[] {
  const out: ValueTok[] = []
  let last = 0
  let m: RegExpExecArray | null
  TOKEN_RE.lastIndex = 0
  while ((m = TOKEN_RE.exec(v))) {
    if (m.index > last) out.push({ t: 'txt', raw: v.slice(last, m.index) })
    const tok = m[0]
    if (tok[0] === '#' || tok.startsWith('rgb') || tok.startsWith('hsl')) {
      out.push({ t: 'color', raw: tok })
    } else {
      const nm = tok.match(/^(-?[\d.]+)([a-z%]*)$/i)
      if (nm && Number.isFinite(parseFloat(nm[1]))) out.push({ t: 'num', raw: tok, num: parseFloat(nm[1]), unit: nm[2] })
      else out.push({ t: 'txt', raw: tok })
    }
    last = m.index + tok.length
  }
  if (last < v.length) out.push({ t: 'txt', raw: v.slice(last) })
  return out
}

type Rgba = { r: number; g: number; b: number; a: number }

/** Parse ANY CSS color (hex, named, rgb/rgba, hsl/hsla) to {r,g,b,a} using
 *  the browser's own parser — no color-space math to maintain. */
function parseColorToRgba(str: string): Rgba {
  if (typeof document === 'undefined') return { r: 0, g: 0, b: 0, a: 1 }
  const el = document.createElement('div')
  el.style.color = str
  document.body.appendChild(el)
  const c = getComputedStyle(el).color
  el.remove()
  const m = c.match(/rgba?\(([^)]+)\)/i)
  if (!m) return { r: 0, g: 0, b: 0, a: 1 }
  const p = m[1].split(',').map((s) => parseFloat(s))
  return { r: p[0] || 0, g: p[1] || 0, b: p[2] || 0, a: p[3] === undefined ? 1 : p[3] }
}
function formatRgba({ r, g, b, a }: Rgba): string {
  const n = (x: number) => Math.round(x)
  return a >= 1 ? `rgb(${n(r)}, ${n(g)}, ${n(b)})` : `rgba(${n(r)}, ${n(g)}, ${n(b)}, ${Math.round(a * 100) / 100})`
}

// Compact overrides for react-colorful (its defaults are too large/chunky
// for this panel): smaller board, thinner hue/alpha bars with breathing
// room between them, smaller thumbs.
const COLOR_PICKER_CSS = `
.papan-cp .react-colorful { width: 168px; height: 168px; }
.papan-cp .react-colorful__saturation { border-radius: 4px; }
.papan-cp .react-colorful__hue { height: 12px; border-radius: 4px; margin-top: 8px; }
.papan-cp .react-colorful__alpha { height: 12px; border-radius: 4px; margin-top: 8px; }
.papan-cp .react-colorful__pointer { width: 12px; height: 12px; }
`

/** A color chip that opens an ALPHA-capable picker (react-colorful) in a
 *  popover — the native `<input type=color>` can't do alpha. The popover is
 *  PORTALED to <body> so no ancestor's overflow/containing-block can clip
 *  it; click-outside stays correct via the `data-papan-cp` marker (the
 *  value cell's own outside-handler ignores it) and the picker's DOM ref. */
function ColorSwatch({ color, onChange }: { color: string; onChange: (c: string) => void }) {
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null)
  const rootRef = useRef<HTMLSpanElement>(null)
  const pickerRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!pos) return
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node
      if (rootRef.current?.contains(t) || pickerRef.current?.contains(t)) return
      setPos(null)
    }
    document.addEventListener('mousedown', onDown, true)
    return () => document.removeEventListener('mousedown', onDown, true)
  }, [pos])
  const PW = 184, PH = 224
  return (
    <span ref={rootRef} className="relative" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        className="inline-block align-middle mr-0.5 w-2.5 h-2.5 rounded-sm border themed-border-secondary cursor-pointer"
        style={{ background: color }}
        data-tooltip="Edit color"
        aria-label="Edit color"
        onClick={(e) => {
          e.stopPropagation()
          if (pos) { setPos(null); return }
          const r = e.currentTarget.getBoundingClientRect()
          // Right-anchor to the swatch (the panel hugs the screen's right
          // edge) and flip above if it'd overflow the bottom.
          const left = Math.max(8, Math.min(r.right - PW, window.innerWidth - PW - 8))
          const below = r.bottom + 4
          const top = below + PH > window.innerHeight - 8 ? Math.max(8, r.top - PH - 4) : below
          setPos({ left, top })
        }}
      />
      {pos && createPortal(
        <div
          ref={pickerRef}
          data-papan-cp=""
          className="papan-cp fixed z-[70] p-2 rounded-md shadow-xl"
          style={{ left: pos.left, top: pos.top, width: PW, background: '#18181b', border: '1px solid #3f3f46' }}
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <style>{COLOR_PICKER_CSS}</style>
          <RgbaColorPicker color={parseColorToRgba(color)} onChange={(c) => onChange(formatRgba(c))} />
          <input
            className="rounded w-full mt-2 px-1 py-0.5 text-[10px] font-mono outline-none"
            style={{ background: '#27272a', color: '#e4e4e7', border: '1px solid #3f3f46' }}
            value={color}
            spellCheck={false}
            onChange={(e) => onChange(e.target.value)}
          />
        </div>,
        document.body,
      )}
    </span>
  )
}

/** The tokenized, interactive rendering of one CSS value. Numbers scrub
 *  (drag, Shift = ×10; unitless decimals step 0.1), color tokens carry a
 *  picker swatch. Same component inline and in the hover overlay. */
function TokenView({ value, onChange, wrap }: { value: string; onChange: (v: string) => void; wrap?: boolean }) {
  // Scrub math always runs off the tokens captured at drag START — the
  // committed value changes under the pointer as it moves.
  const baseRef = useRef<ValueTok[] | null>(null)
  const toks = tokenizeValue(value)
  const rebuild = (base: ValueTok[], i: number, raw: string) =>
    base.map((b, j) => (j === i ? raw : b.raw)).join('')
  return (
    <span className={wrap ? 'whitespace-normal break-words' : 'whitespace-nowrap'}>
      {toks.map((tok, i) => {
        if (tok.t === 'num') {
          return (
            <span
              key={i}
              className="underline decoration-dotted decoration-1 underline-offset-2 cursor-ew-resize"
              title="Drag to adjust · Shift = ×10"
              onClick={(e) => e.stopPropagation()}
              onPointerDown={(e) => {
                baseRef.current = tokenizeValue(value)
                e.currentTarget.setPointerCapture(e.pointerId)
                e.preventDefault()
                e.stopPropagation()
                ;(e.currentTarget as HTMLElement).dataset.x = String(e.clientX)
              }}
              onPointerMove={(e) => {
                const base = baseRef.current
                const x0 = parseFloat((e.currentTarget as HTMLElement).dataset.x || '')
                if (!base || !Number.isFinite(x0)) return
                const bt = base[i]
                if (!bt || bt.t !== 'num') return
                const isDec = bt.raw.includes('.') && !bt.unit
                const step = (isDec ? 0.1 : 1) * (e.shiftKey ? 10 : 1)
                const n = Math.round((bt.num + Math.round((e.clientX - x0) / 3) * step) * 100) / 100
                onChange(rebuild(base, i, `${n}${bt.unit}`))
              }}
              onPointerUp={(e) => {
                baseRef.current = null
                delete (e.currentTarget as HTMLElement).dataset.x
              }}
            >
              {tok.raw}
            </span>
          )
        }
        if (tok.t === 'color') {
          return (
            <span key={i} className="whitespace-nowrap">
              <ColorSwatch color={tok.raw} onChange={(c) => onChange(rebuild(toks, i, c))} />
              {tok.raw}
            </span>
          )
        }
        return <span key={i}>{tok.raw}</span>
      })}
    </span>
  )
}

/** User-facing property label: strip the section's own prefix (Background's
 *  `background-color` → "Color"), drop a vendor prefix, de-hyphenate, and
 *  capitalize — so the panel never shows raw CSS identifiers. */
function humanLabel(prop: string, stripPrefix?: string): string {
  let p = prop
  if (stripPrefix && p.startsWith(stripPrefix + '-')) p = p.slice(stripPrefix.length + 1)
  p = p.replace(/^-webkit-|^-moz-|^-ms-/, '')
  p = p.replace(/-/g, ' ').trim()
  return p ? p.charAt(0).toUpperCase() + p.slice(1) : humanLabel(prop.replace(/^-+/, ''))
}

/** The interactive value cell shared by every property row AND the Size
 *  fields: a scrubbable, tokenized string. Numbers drag (keeping their
 *  unit), colors carry a picker swatch, a known-enum value shows a
 *  dropdown, double-click edits the raw string (with suggestions), and a
 *  truncated value grows a hover overlay. When `scrubSeed` is set, dragging
 *  an unset/keyword value (no number to grab) synthesizes `<seed>px` — the
 *  "drag an auto field into a value" gesture. Validated through the
 *  browser's own engine; invalid input shows red and never commits. */
function ValueEditor({ prop, value, placeholder = '—', suggest = [], enums, scrubSeed, after, onCommit }: {
  prop: string
  value: string
  placeholder?: string
  suggest?: string[]
  enums?: string[]
  scrubSeed?: number
  /** Rendered after the value when NOT editing (e.g. a remove button). */
  after?: React.ReactNode
  onCommit: (value: string) => void
}) {
  const [v, setV] = useState(value)
  const [editing, setEditing] = useState(false)
  const [invalid, setInvalid] = useState(false)
  const [overlayAt, setOverlayAt] = useState<{ left: number; top: number } | null>(null)
  // Edit popover is anchored by its RIGHT edge (the value's right edge) so
  // it can grow leftward as the text lengthens without drifting.
  const [editAt, setEditAt] = useState<{ right: number; top: number } | null>(null)
  const valRef = useRef<HTMLSpanElement>(null)
  const overlayRef = useRef<HTMLDivElement>(null)
  const seedDrag = useRef<{ x: number; moved: boolean } | null>(null)
  // Re-seed when the live value changes elsewhere (a shorthand apply, a
  // sibling control) — this cell is a VIEW of the shared state, not an owner.
  const lastSeed = useRef(value)
  if (lastSeed.current !== value) {
    lastSeed.current = value
    if (!editing && v !== value) setV(value)
  }

  const change = (val: string) => {
    setV(val)
    const trimmed = val.trim()
    const ok = trimmed === '' || (typeof CSS !== 'undefined' && CSS.supports ? CSS.supports(prop, trimmed) : true)
    setInvalid(!ok)
    if (ok) onCommit(trimmed)
  }

  // Popovers are RIGHT-aligned to the value (the panel hugs the right edge,
  // so a left-anchored panel would spill off-screen) and clamped to the
  // viewport.
  const OVERLAY_W = 264
  const anchorRight = (r: DOMRect, width: number) =>
    Math.max(8, Math.min(r.right - width, window.innerWidth - width - 8))

  // A truncated value opens a PERSISTENT dark overlay on click — the same
  // token view, unclipped — so its cut-off numbers/colors are reachable.
  // Click-triggered (not hover) so it survives reaching for a color swatch
  // or the native picker; a click outside closes it.
  const toggleOverlay = () => {
    if (overlayAt) { setOverlayAt(null); return }
    const el = valRef.current
    if (!el || el.scrollWidth <= el.clientWidth + 1) return // not truncated — nothing to reveal
    const r = el.getBoundingClientRect()
    setOverlayAt({ left: anchorRight(r, OVERLAY_W), top: r.top - 4 })
  }
  useEffect(() => {
    if (!overlayAt) return
    const onDown = (e: MouseEvent) => {
      const t = e.target as HTMLElement
      if (overlayRef.current?.contains(t) || valRef.current?.contains(t)) return
      if (t.closest?.('[data-papan-cp]')) return // a portaled color picker is "inside"
      setOverlayAt(null)
    }
    document.addEventListener('mousedown', onDown, true)
    return () => document.removeEventListener('mousedown', onDown, true)
  }, [overlayAt])

  // Editing opens a popover anchored to the value — the row itself is far
  // too narrow for a long value string. It sizes to its content (with a
  // sensible min/max), growing leftward from the value's right edge.
  const startEdit = (anchor?: HTMLElement | null) => {
    const r = (anchor ?? valRef.current)?.getBoundingClientRect()
    setEditAt(r ? { right: r.right, top: r.top - 4 } : { right: window.innerWidth - 8, top: 8 })
    setOverlayAt(null)
    setEditing(true)
  }

  const enumMode = !editing && enums && enums.includes(v.trim())
  const canSeed = scrubSeed !== undefined

  if (editing) {
    // Size to content: ~6.2px per monospace char at text-[10px] + padding,
    // clamped between a readable minimum and the viewport.
    const vw = typeof window !== 'undefined' ? window.innerWidth : 1024
    const editW = Math.min(Math.max(140, v.length * 6.2 + 26), Math.min(vw - 16, 460))
    const editLeft = editAt ? Math.max(8, Math.min(editAt.right - editW, vw - editW - 8)) : 8
    return (
      <>
        <span ref={valRef} className="flex-1 min-w-0 truncate font-mono text-[10px] px-1 py-0.5 rounded themed-text-muted select-none">
          {v || placeholder}
        </span>
        {after}
        <div
          className="fixed z-50 rounded-md shadow-xl p-1.5"
          style={{ left: editLeft, top: editAt?.top ?? 8, width: editW, background: '#18181b', border: '1px solid #3f3f46' }}
        >
          <input
            type="text"
            autoFocus
            list={`papan-style-${prop}`}
            className={`themed-input rounded w-full px-1 py-0.5 text-[10px] font-mono ${invalid ? 'text-red-400' : ''}`}
            value={v}
            onChange={(e) => change(e.target.value)}
            onBlur={() => setEditing(false)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'Escape') setEditing(false) }}
          />
          <datalist id={`papan-style-${prop}`}>
            {(suggest.length ? suggest : enums || []).map((sg) => <option key={sg} value={sg} />)}
          </datalist>
        </div>
      </>
    )
  }
  if (enumMode) {
    return (
      <>
        <select
          className="themed-input rounded px-1 py-0.5 text-[10px] flex-1 min-w-0"
          value={v.trim()}
          onDoubleClick={(e) => startEdit(e.currentTarget)}
          onChange={(e) => change(e.target.value)}
        >
          {enums!.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
        {after}
      </>
    )
  }
  return (
    <>
      <span
        ref={valRef}
        className={`flex-1 min-w-0 truncate font-mono text-[10px] px-1 py-0.5 rounded select-none ${canSeed && !/-?\d/.test(v) ? 'cursor-ew-resize' : ''} ${invalid ? 'text-red-400' : v ? 'themed-text' : 'themed-text-muted'}`}
        title={canSeed ? 'Drag to set · double-click to type' : 'Click to expand · double-click to edit'}
        onClick={toggleOverlay}
        onDoubleClick={() => startEdit()}
        onPointerDown={(e) => {
          // Drag-to-set only when there's no number token to grab (a set
          // value's own number spans handle their own scrub). Seeds from
          // the rendered px so an `auto`/`none` field drags into a value.
          if (!canSeed || /-?\d/.test(v)) return
          seedDrag.current = { x: e.clientX, moved: false }
          e.currentTarget.setPointerCapture(e.pointerId)
        }}
        onPointerMove={(e) => {
          const d = seedDrag.current
          if (!d) return
          const dx = e.clientX - d.x
          if (!d.moved && Math.abs(dx) < 3) return
          d.moved = true
          const n = Math.max(0, Math.round(scrubSeed! + Math.round(dx / 3) * (e.shiftKey ? 10 : 1)))
          change(`${n}px`)
        }}
        onPointerUp={() => { seedDrag.current = null }}
      >
        {v ? <TokenView value={v} onChange={change} /> : placeholder}
      </span>
      {after}
      {overlayAt && (
        <div
          ref={overlayRef}
          className="fixed z-50 rounded-md shadow-xl px-2 py-1.5 font-mono text-[10px]"
          style={{ left: overlayAt.left, top: overlayAt.top, width: OVERLAY_W, background: '#18181b', color: '#e4e4e7', border: '1px solid #3f3f46' }}
          onDoubleClick={() => startEdit()}
        >
          <TokenView value={v} onChange={change} wrap />
        </div>
      )}
    </>
  )
}

function StyleRow({ prop, label, initial, isSet, suggest, enums, onCommit }: {
  prop: string
  /** User-facing display label (defaults to the humanized prop). */
  label?: string
  initial: string
  isSet: boolean
  suggest: string[]
  enums?: string[]
  onCommit: (value: string) => void
}) {
  return (
    <div className="flex items-center gap-1 min-w-0">
      <span className={`text-[10px] w-[4.5rem] shrink-0 truncate ${isSet ? 'themed-text font-medium' : 'themed-text-muted'}`} title={prop}>
        {label ?? humanLabel(prop)}
      </span>
      <ValueEditor
        prop={prop}
        value={initial}
        suggest={suggest}
        enums={enums}
        onCommit={onCommit}
        after={isSet ? (
          <button
            type="button"
            data-tooltip={`Remove ${label ?? humanLabel(prop)}`}
            aria-label={`Remove ${label ?? humanLabel(prop)}`}
            className="themed-text-muted hover:themed-text cursor-pointer shrink-0"
            onClick={() => onCommit('')}
          >
            <X size={9} />
          </button>
        ) : undefined}
      />
    </div>
  )
}

function MoreStylesSection({ styles, live, setProp, setPropBatch }: {
  styles: Record<string, string>
  live: Record<string, string>
  setProp: (prop: string, value: string) => void
  setPropBatch: (decls: Array<{ prop: string; value: string }>) => void
}) {
  const [pasteOpen, setPasteOpen] = useState(false)
  const [pasteText, setPasteText] = useState('')
  // Inline decls beyond the defaults (pasted or set elsewhere) get their
  // own rows — nothing the element carries is invisible, but everything a
  // SECTION owns stays in its section (EXTRA_ROW_EXCLUDE).
  const extras = Object.keys(live).filter(
    (p) => !MORE_ROWS.some((r) => r.prop === p) && !EXTRA_ROW_EXCLUDE.test(p),
  )

  const applyPaste = () => {
    // Accept a declarations block, with or without a selector wrapper —
    // exactly what people copy from tutorials/snippets.
    const body = pasteText.replace(/\/\*[\s\S]*?\*\//g, '')
    const inner = body.includes('{')
      ? body.slice(body.indexOf('{') + 1, body.lastIndexOf('}') > 0 ? body.lastIndexOf('}') : body.length)
      : body
    const decls: Array<{ prop: string; value: string }> = []
    for (const part of inner.split(';')) {
      const i = part.indexOf(':')
      if (i < 0) continue
      const p = part.slice(0, i).trim().toLowerCase()
      const val = part.slice(i + 1).trim()
      if (!/^-?[a-z][a-z-]{0,39}$/.test(p) || val.length > 120) continue
      if (typeof CSS !== 'undefined' && CSS.supports && !CSS.supports(p, val)) continue
      decls.push({ prop: p, value: val })
      if (decls.length >= 20) break
    }
    if (decls.length) setPropBatch(decls)
    setPasteOpen(false)
    setPasteText('')
  }

  return (
    <Section
      title="More styles"
      action={
        <button
          type="button"
          data-tooltip="Add CSS declarations — paste or type any property"
          aria-label="Add CSS declarations — paste or type any property"
          className="themed-text-muted hover:themed-text cursor-pointer"
          onClick={() => setPasteOpen((o) => !o)}
        >
          <Plus size={12} />
        </button>
      }
    >
      {pasteOpen && (
        <div className="mb-1.5">
          <textarea
            autoFocus
            rows={4}
            className="themed-input rounded w-full text-[10px] font-mono p-1"
            placeholder={'filter: blur(4px);\ntransition: all 0.2s ease;\nbackdrop-filter: brightness(1.1);'}
            value={pasteText}
            onChange={(e) => setPasteText(e.target.value)}
          />
          <div className="flex gap-2 justify-end mt-1">
            <button type="button" className="text-[10px] themed-text-muted hover:themed-text cursor-pointer" onClick={() => { setPasteOpen(false); setPasteText('') }}>
              Cancel
            </button>
            <button type="button" className="text-[10px] themed-btn-primary rounded px-2 py-0.5 cursor-pointer" onClick={applyPaste}>
              Apply
            </button>
          </div>
        </div>
      )}
      <div className="flex flex-col gap-0.5">
        {MORE_ROWS.map((r) => (
          <StyleRow
            key={r.prop}
            prop={r.prop}
            label={humanLabel(r.prop)}
            suggest={r.suggest}
            enums={r.enums}
            initial={live[r.prop] ?? styles[r.key] ?? ''}
            isSet={r.prop in live}
            onCommit={(val) => setProp(r.prop, val)}
          />
        ))}
        {extras.map((p) => (
          <StyleRow key={p} prop={p} label={humanLabel(p)} suggest={[]} initial={live[p]} isSet onCommit={(val) => setProp(p, val)} />
        ))}
      </div>
    </Section>
  )
}

const ALIGNS = [
  { value: 'left', icon: AlignLeft },
  { value: 'center', icon: AlignCenter },
  { value: 'right', icon: AlignRight },
] as const

/** Compose the shortest shorthand from computed per-side values —
 *  seeds a section's raw-edit row when nothing is set inline. */
/** Collapse four side values into the shortest CSS box shorthand. */
function collapse4(t: string, r: string, b: string, l: string): string {
  if (t === r && r === b && b === l) return t
  if (t === b && r === l) return `${t} ${r}`
  return `${t} ${r} ${b} ${l}`
}

function composeSides(styles: Record<string, string>, prefix: 'padding' | 'margin'): string {
  return collapse4(
    styles[`${prefix}Top`] || '0px',
    styles[`${prefix}Right`] || '0px',
    styles[`${prefix}Bottom`] || '0px',
    styles[`${prefix}Left`] || '0px',
  )
}

/** `padding-top` → `paddingTop` (computed-style key). */
function camelKey(prop: string): string {
  return prop.replace(/-([a-z])/g, (_, c: string) => c.toUpperCase())
}

interface PropSpec { prop: string; computedKey?: string; enums?: string[]; suggest?: string[] }

// Section-owned breakdown rows (the "common acceptable parameters"). User
// input via the pencil can add rows beyond these — the union renders.
const BG_ROWS: PropSpec[] = [
  { prop: 'background-color', computedKey: 'backgroundColor' },
  { prop: 'background-image', computedKey: 'backgroundImage', suggest: ['none', 'linear-gradient(135deg, #6366f1, #ec4899)', 'url()'] },
  { prop: 'background-clip', computedKey: 'backgroundClip', enums: ['border-box', 'padding-box', 'content-box', 'text'] },
]
// Image-only sub-properties — revealed once an image/gradient is present
// (they're inert and just noise on a plain color background).
const BG_IMAGE_ROWS: PropSpec[] = [
  { prop: 'background-size', computedKey: 'backgroundSize', suggest: ['auto', 'cover', 'contain'] },
  { prop: 'background-position', computedKey: 'backgroundPosition', suggest: ['center', 'top left', '50% 50%'] },
  { prop: 'background-repeat', computedKey: 'backgroundRepeat', enums: ['repeat', 'no-repeat', 'repeat-x', 'repeat-y', 'space', 'round'] },
  { prop: 'background-attachment', computedKey: 'backgroundAttachment', enums: ['scroll', 'fixed', 'local'] },
]
const BORDER_ROWS: PropSpec[] = [
  { prop: 'border-width', computedKey: 'borderWidth' },
  { prop: 'border-style', computedKey: 'borderStyle', enums: ['none', 'solid', 'dashed', 'dotted', 'double'] },
  { prop: 'border-color', computedKey: 'borderColor' },
  { prop: 'border-radius', computedKey: 'borderRadius' },
  { prop: 'outline', computedKey: 'outline', suggest: ['none', '1px solid #6366f1', '2px dashed #a1a1aa'] },
]
// Common font-family stacks offered as suggestions on the Font row.
const FONT_FAMILIES = ['inherit', 'system-ui, sans-serif', 'Arial, Helvetica, sans-serif', 'Georgia, serif', 'Times New Roman, serif', 'ui-monospace, SFMono-Regular, monospace', 'Courier New, monospace']

const TEXT_ROWS: PropSpec[] = [
  { prop: 'font-family', computedKey: 'fontFamily', suggest: FONT_FAMILIES },
  { prop: 'color', computedKey: 'color' },
  { prop: 'font-size', computedKey: 'fontSize', suggest: ['12px', '14px', '16px', '20px', '24px', '32px'] },
  { prop: 'font-weight', computedKey: 'fontWeight', enums: ['100', '200', '300', '400', '500', '600', '700', '800', '900'] },
  { prop: 'line-height', computedKey: 'lineHeight', suggest: ['normal', '1.2', '1.5', '2'] },
  { prop: 'letter-spacing', computedKey: 'letterSpacing', suggest: ['normal', '0.05em', '0.1em', '-0.02em'] },
  { prop: 'word-spacing', computedKey: 'wordSpacing', suggest: ['normal', '0.1em', '0.25em'] },
  { prop: 'text-wrap', computedKey: 'textWrap', enums: ['wrap', 'nowrap', 'balance', 'pretty'] },
  { prop: 'text-shadow', computedKey: 'textShadow', suggest: ['none', '0 1px 2px rgba(0,0,0,0.4)', '0 2px 8px rgba(0,0,0,0.3)'] },
  { prop: 'text-overflow', computedKey: 'textOverflow', enums: ['clip', 'ellipsis'] },
]

// ─── Box-shadow (value-parsed, not a longhand shorthand) ───────────────
// box-shadow has no per-part CSS longhands, so we PARSE the value:
// `[inset] <x> <y> <blur> <spread> <color>` (color may serialize first, as
// getComputedStyle does). Multiple comma-separated shadows are supported —
// the fields edit the FIRST; the pencil edits the whole string.

/** Split on top-level `sep`, ignoring separators inside parens (rgba(…)). */
function splitTopLevel(s: string, sep: string): string[] {
  const out: string[] = []
  let depth = 0, cur = ''
  for (const ch of s) {
    if (ch === '(') depth++
    else if (ch === ')') depth--
    if (ch === sep && depth === 0) { out.push(cur); cur = '' }
    else cur += ch
  }
  if (cur.trim()) out.push(cur)
  return out
}

interface Shadow { inset: boolean; x: string; y: string; blur: string; spread: string; color: string }
function parseShadow(s: string): Shadow {
  let str = ` ${s.trim()} `
  const inset = /\binset\b/.test(str)
  str = str.replace(/\binset\b/, ' ')
  const cm = str.match(/#[0-9a-fA-F]{3,8}\b|rgba?\([^)]*\)|hsla?\([^)]*\)/)
  let color = ''
  if (cm) { color = cm[0]; str = str.replace(cm[0], ' ') }
  const n: string[] = []
  for (const t of str.trim().split(/\s+/).filter(Boolean)) {
    if (/^-?[\d.]+[a-z%]*$/i.test(t)) n.push(t)
    else if (!color) color = t // a named color keyword (e.g. black)
  }
  if (!color) color = 'rgba(0, 0, 0, 0.2)'
  return { inset, x: n[0] || '0px', y: n[1] || '0px', blur: n[2] || '0px', spread: n[3] || '0px', color }
}
function buildShadow(sh: Shadow): string {
  return `${sh.inset ? 'inset ' : ''}${sh.x} ${sh.y} ${sh.blur} ${sh.spread} ${sh.color}`
}

/** Labeled scrubbable px field for a shadow part. */
function ShadowNum({ label, value, onCommit }: { label: string; value: string; onCommit: (v: string) => void }) {
  const n = parseFloat(value)
  return (
    <label className="flex flex-col gap-0.5 flex-1 min-w-0">
      <span className="text-[9px] themed-text-muted text-center">{label}</span>
      <NumScrub value={Number.isFinite(n) ? String(n) : ''} placeholder="0" onCommit={(raw) => onCommit(raw === '' ? '0px' : `${raw}px`)} />
    </label>
  )
}

function BoxShadowSection({ value, onCommit, action, popover }: {
  value: string
  onCommit: (v: string) => void
  action: React.ReactNode
  popover: React.ReactNode
}) {
  const shadows = splitTopLevel(value && value !== 'none' ? value : '', ',')
  const rest = shadows.slice(1)
  const sh = parseShadow(shadows[0] || '')
  const commit = (next: Shadow) => onCommit([buildShadow(next), ...rest].map((s) => s.trim()).filter(Boolean).join(', ') || 'none')
  const set = (patch: Partial<Shadow>) => commit({ ...sh, ...patch })
  return (
    <Section title="Box shadow" action={action}>
      {popover}
      {shadows.length > 1 && (
        <div className="text-[9px] themed-text-muted mb-1">Editing the first of {shadows.length} shadows — use ✎ for all</div>
      )}
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            title="Inset (inner shadow)"
            className={`px-1.5 py-0.5 rounded text-[10px] border cursor-pointer ${sh.inset ? 'themed-bg-tertiary themed-text border-transparent' : 'themed-text-muted themed-border-secondary hover:themed-text'}`}
            onClick={() => set({ inset: !sh.inset })}
          >
            Inset
          </button>
          <div className="flex items-center gap-1 flex-1 min-w-0 font-mono text-[10px] themed-text-muted">
            <ColorSwatch color={sh.color} onChange={(c) => set({ color: c })} />
            <span className="truncate">{sh.color}</span>
          </div>
        </div>
        <div className="flex gap-1">
          <ShadowNum label="X" value={sh.x} onCommit={(v) => set({ x: v })} />
          <ShadowNum label="Y" value={sh.y} onCommit={(v) => set({ y: v })} />
          <ShadowNum label="Blur" value={sh.blur} onCommit={(v) => set({ blur: v })} />
          <ShadowNum label="Spread" value={sh.spread} onCommit={(v) => set({ spread: v })} />
        </div>
      </div>
    </Section>
  )
}

// ─── Transform (value-parsed) ──────────────────────────────────────────
// Also value-parsed: getComputedStyle serializes transform to a matrix(),
// so we decompose that (translate/rotate/scale/skew); an authored function
// string (translate(…) rotate(…)) is parsed directly. Rebuilt as functions
// on every edit.
interface Xform { tx: string; ty: string; rotate: string; sx: string; sy: string; skewX: string; skewY: string }
const XFORM_DEFAULT: Xform = { tx: '0px', ty: '0px', rotate: '0deg', sx: '1', sy: '1', skewX: '0deg', skewY: '0deg' }

function parseTransform(v: string): Xform {
  if (!v || v === 'none') return { ...XFORM_DEFAULT }
  const mm = v.match(/^matrix\(([^)]+)\)$/)
  if (mm) {
    const [a, b, c, d, e, f] = mm[1].split(',').map((x) => parseFloat(x))
    const round = (n: number) => Math.round((n + 0) * 1000) / 1000
    const scaleX = Math.hypot(a, b) || 1
    const det = a * d - b * c
    return {
      tx: `${round(e)}px`, ty: `${round(f)}px`,
      rotate: `${round(Math.atan2(b, a) * 180 / Math.PI)}deg`,
      sx: String(round(scaleX)), sy: String(round(det / scaleX || 1)),
      skewX: `${round(Math.atan2(a * c + b * d, a * a + b * b) * 180 / Math.PI)}deg`, skewY: '0deg',
    }
  }
  const out = { ...XFORM_DEFAULT }
  const fn = (name: string): string[] | null => {
    const m = v.match(new RegExp(`(?:^|\\s)${name}\\(([^)]+)\\)`))
    return m ? m[1].split(',').map((s) => s.trim()) : null
  }
  const tr = fn('translate'); if (tr) { out.tx = tr[0]; out.ty = tr[1] ?? '0px' }
  const trX = fn('translateX'); if (trX) out.tx = trX[0]
  const trY = fn('translateY'); if (trY) out.ty = trY[0]
  const rot = fn('rotate') ?? fn('rotateZ'); if (rot) out.rotate = rot[0]
  const sc = fn('scale'); if (sc) { out.sx = sc[0]; out.sy = sc[1] ?? sc[0] }
  const scX = fn('scaleX'); if (scX) out.sx = scX[0]
  const scY = fn('scaleY'); if (scY) out.sy = scY[0]
  const sk = fn('skew'); if (sk) { out.skewX = sk[0]; out.skewY = sk[1] ?? '0deg' }
  const skX = fn('skewX'); if (skX) out.skewX = skX[0]
  const skY = fn('skewY'); if (skY) out.skewY = skY[0]
  return out
}
function buildTransform(t: Xform): string {
  const parts: string[] = []
  if ((parseFloat(t.tx) || 0) !== 0 || (parseFloat(t.ty) || 0) !== 0) parts.push(`translate(${t.tx || '0px'}, ${t.ty || '0px'})`)
  if ((parseFloat(t.rotate) || 0) !== 0) parts.push(`rotate(${t.rotate})`)
  if ((parseFloat(t.sx) || 1) !== 1 || (parseFloat(t.sy) || 1) !== 1) parts.push(`scale(${t.sx}, ${t.sy})`)
  if ((parseFloat(t.skewX) || 0) !== 0 || (parseFloat(t.skewY) || 0) !== 0) parts.push(`skew(${t.skewX}, ${t.skewY})`)
  return parts.length ? parts.join(' ') : 'none'
}

/** Labeled scrub field with a fixed unit suffix (px / deg / unitless). */
function UnitNum({ label, value, unit, step, onCommit }: { label: string; value: string; unit: string; step?: number; onCommit: (v: string) => void }) {
  const n = parseFloat(value)
  return (
    <label className="flex flex-col gap-0.5 flex-1 min-w-0">
      <span className="text-[9px] themed-text-muted text-center">{label}</span>
      <NumScrub value={Number.isFinite(n) ? String(n) : ''} placeholder="0" step={step} onCommit={(raw) => onCommit(raw === '' ? `0${unit}` : `${raw}${unit}`)} />
    </label>
  )
}

function TransformSection({ value, onCommit, action, popover }: {
  value: string
  onCommit: (v: string) => void
  action: React.ReactNode
  popover: React.ReactNode
}) {
  const t = parseTransform(value)
  const set = (patch: Partial<Xform>) => onCommit(buildTransform({ ...t, ...patch }))
  return (
    <Section title="Transform" action={action}>
      {popover}
      <div className="flex flex-col gap-1">
        <div className="flex gap-1">
          <UnitNum label="Move X" value={t.tx} unit="px" onCommit={(v) => set({ tx: v })} />
          <UnitNum label="Move Y" value={t.ty} unit="px" onCommit={(v) => set({ ty: v })} />
        </div>
        <div className="flex gap-1">
          <UnitNum label="Scale X" value={t.sx} unit="" step={0.1} onCommit={(v) => set({ sx: v })} />
          <UnitNum label="Scale Y" value={t.sy} unit="" step={0.1} onCommit={(v) => set({ sy: v })} />
        </div>
        <div className="flex gap-1">
          <UnitNum label="Rotate" value={t.rotate} unit="deg" onCommit={(v) => set({ rotate: v })} />
          <UnitNum label="Skew X" value={t.skewX} unit="deg" onCommit={(v) => set({ skewX: v })} />
          <UnitNum label="Skew Y" value={t.skewY} unit="deg" onCommit={(v) => set({ skewY: v })} />
        </div>
      </div>
    </Section>
  )
}

export function DesignPanel({ selection, onStyle, onStyleBatch, scrollStore }: DesignPanelProps) {
  const s = selection.styles

  // Preserve scroll across the keyed remount (see `scrollStore` prop). Runs
  // on each mount: same element → restore the saved offset; new element →
  // start at top and reset the store. onScroll keeps the store current.
  const scrollElRef = useRef<HTMLDivElement>(null)
  useLayoutEffect(() => {
    const el = scrollElRef.current
    if (!el || !scrollStore) return
    if (scrollStore.current.loc === selection.locAttr) el.scrollTop = scrollStore.current.top
    else scrollStore.current = { loc: selection.locAttr, top: 0 }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  // computed text-align can be 'start'/'end' — normalize for the buttons
  const initialAlign = s.textAlign === 'start' ? 'left' : s.textAlign === 'end' ? 'right' : (s.textAlign || 'left')
  const [align, setAlign] = useState(initialAlign)
  // B / I / U / S toggles. Bold rides font-weight (shared with the weight
  // row in the list); underline + strikethrough are both
  // text-decoration-line, kept as a set. `bold`/`commitDeco` are derived
  // below, once `live`/`setProp` exist.
  const [italic, setItalic] = useState(s.fontStyle === 'italic' || s.fontStyle === 'oblique')
  const initDeco = (s.textDecorationLine || '').split(' ')
  const [underline, setUnderline] = useState(initDeco.includes('underline'))
  const [strike, setStrike] = useState(initDeco.includes('line-through'))

  // ── ONE source of truth: the element's inline declarations, mirrored
  // live. Every control writes through setProp/setPropBatch (which also
  // updates this map), and every control READS through effective() —
  // explicit longhand → expansion of a set shorthand → computed value.
  // No control owns private truth, so the per-side grid, the breakdown
  // rows and the pencil's shorthand input can never disagree.
  const [live, setLive] = useState<Record<string, string>>(() => parseInlineDecls(selection.inlineStyle))
  const updateLive = (decls: Array<{ prop: string; value: string }>) => {
    setLive((m) => {
      const n = { ...m }
      for (const d of decls) {
        if (d.value) n[d.prop] = d.value
        else delete n[d.prop]
      }
      return n
    })
  }
  // Single prop rides the debounced per-prop pipeline (scrub-friendly);
  // batches (shorthand apply, CSS paste) commit atomically.
  const setProp = (prop: string, value: string) => {
    updateLive([{ prop, value }])
    onStyle(prop, value)
  }
  const setPropBatch = (decls: Array<{ prop: string; value: string }>) => {
    updateLive(decls)
    if (onStyleBatch) onStyleBatch(decls)
    else for (const d of decls) onStyle(d.prop, d.value)
  }
  const effective = (prop: string, computedKey?: string): string => {
    if (live[prop] !== undefined) return live[prop]
    const sh = SHORTHAND_OF[prop]
    if (sh && live[sh] !== undefined) {
      const ex = expandShorthand(sh, live[sh], prop)
      if (ex) return ex
    }
    return computedKey ? (s[computedKey] ?? '') : ''
  }
  // "Set" = an explicit inline longhand (bold + a working remove button).
  // A value merely covered by a stored shorthand shows (via effective) but
  // isn't removable here — you'd edit the shorthand via the pencil.
  const isSetProp = (prop: string): boolean => live[prop] !== undefined
  const propRow = (spec: PropSpec, prefix?: string) => (
    <StyleRow
      key={spec.prop}
      prop={spec.prop}
      label={humanLabel(spec.prop, prefix)}
      suggest={spec.suggest ?? []}
      enums={spec.enums}
      initial={effective(spec.prop, spec.computedKey)}
      isSet={isSetProp(spec.prop)}
      onCommit={(val) => setProp(spec.prop, val)}
    />
  )
  const ownedExtras = (re: RegExp, defaults: PropSpec[]) =>
    Object.keys(live).filter((p) => re.test(p) && !defaults.some((d) => d.prop === p))

  // Bold rides the effective font-weight (kept in sync with the weight row
  // in the list). Underline + strikethrough share text-decoration-line.
  const bold = (parseInt(effective('font-weight', 'fontWeight'), 10) || 400) >= 600
  const commitDeco = (u: boolean, st: boolean) => {
    const parts = [u && 'underline', st && 'line-through'].filter(Boolean)
    setProp('text-decoration-line', parts.length ? parts.join(' ') : 'none')
  }

  // Pencil = edit the section's SHORTHAND in a popover; applying DECOMPOSES
  // it into the longhand rows (one atomic batch) — the shorthand itself is
  // never stored, so the rows stay the single source of truth.
  const [rawOpen, setRawOpen] = useState<Record<string, boolean>>({})
  const pencil = (prop: string) => (
    <button
      type="button"
      data-pencil={prop}
      data-tooltip={`Edit ${humanLabel(prop)} as one line`}
      aria-label={`Edit ${humanLabel(prop)} as one line`}
      className={`cursor-pointer ${rawOpen[prop] ? 'text-indigo-400' : 'themed-text-muted hover:themed-text'}`}
      onClick={() => setRawOpen((o) => ({ ...o, [prop]: !o[prop] }))}
    >
      <Pencil size={10} />
    </button>
  )
  const closePencil = (prop: string) => setRawOpen((o) => ({ ...o, [prop]: false }))
  // `fallbackSeed` covers shorthands whose longhands are always meaningful
  // even when unset inline (padding/margin computed sides); background /
  // border seed empty when nothing is set (a computed "0px none rgb(…)"
  // seed would be noise).
  const shorthandPopover = (shorthand: string, fallbackSeed = '') => rawOpen[shorthand] && (
    <ShorthandPopover
      prop={shorthand}
      seed={composeShorthand(shorthand, live, (lh) => effective(lh, camelKey(lh))) || fallbackSeed}
      onApply={(val) => setPropBatch(decomposeShorthand(shorthand, val))}
      onClose={() => closePencil(shorthand)}
    />
  )

  return (
    <div
      ref={scrollElRef}
      onScroll={(e) => { if (scrollStore) scrollStore.current = { loc: selection.locAttr, top: e.currentTarget.scrollTop } }}
      className="papan-thin-scroll ws-grain ws-design-panel w-60 shrink-0 border-l themed-border-secondary themed-bg-secondary overflow-y-auto text-[11px] themed-text"
    >
      <div className="px-2.5 py-2 border-b themed-border-secondary">
        <div className="font-mono text-indigo-400 truncate" title={selection.selector}>{selection.selector}</div>
        <div className="themed-text-muted text-[10px] mt-0.5">{selection.width}×{selection.height} · L{selection.loc.startLine}</div>
      </div>

      {selection.sizing && (
        <SizeSection sizing={selection.sizing} width={selection.width} height={selection.height} onStyle={setProp} />
      )}

      {selection.layout && (
        <PositionSection layout={selection.layout} authored={authoredInsets(selection.inlineStyle)} onStyle={setProp} />
      )}

      {selection.layout && <LayoutSection layout={selection.layout} onStyle={setProp} />}

      <Section title="Text">
        {/* Alignment (left) · bold/italic/underline/strikethrough (right).
            Font family, color, size & weight live in the row list below. */}
        <div className="flex items-center gap-2 mb-1.5">
          <div className="flex rounded overflow-hidden border themed-border-secondary">
            {ALIGNS.map(({ value, icon: Icon }) => (
              <button
                key={value}
                type="button"
                data-tooltip={`Align ${value}`}
                aria-label={`Align ${value}`}
                className={`px-1.5 py-1 cursor-pointer ${align === value ? 'themed-bg-tertiary themed-text' : 'themed-text-muted hover:themed-text'}`}
                onClick={() => { setAlign(value); setProp('text-align', value) }}
              >
                <Icon size={12} />
              </button>
            ))}
          </div>
          <div className="flex rounded overflow-hidden border themed-border-secondary">
          {([
            { Icon: Bold, on: bold, title: 'Bold', toggle: () => { const nw = bold ? '400' : '700'; setProp('font-weight', nw) } },
            { Icon: Italic, on: italic, title: 'Italic', toggle: () => { const n = !italic; setItalic(n); setProp('font-style', n ? 'italic' : 'normal') } },
            { Icon: Underline, on: underline, title: 'Underline', toggle: () => { const n = !underline; setUnderline(n); commitDeco(n, strike) } },
            { Icon: Strikethrough, on: strike, title: 'Strikethrough', toggle: () => { const n = !strike; setStrike(n); commitDeco(underline, n) } },
          ]).map(({ Icon, on, title, toggle }) => (
            <button
              key={title}
              type="button"
              data-tooltip={title}
              aria-label={title}
              className={`px-1.5 py-1 cursor-pointer ${on ? 'themed-bg-tertiary themed-text' : 'themed-text-muted hover:themed-text'}`}
              onClick={toggle}
            >
              <Icon size={12} />
            </button>
          ))}
          </div>
        </div>
        {VALIGN_DISPLAYS.has(selection.layout?.display ?? '') && (
          <div className="mb-1.5">
            <LayoutSelect label="Vertical align" initial={s.verticalAlign || 'baseline'} options={VERTICAL_ALIGNS} onCommit={(v) => setProp('vertical-align', v)} />
          </div>
        )}
        <div className="flex flex-col gap-0.5">
          {TEXT_ROWS.map((r) => propRow(r))}
        </div>
      </Section>

      <Section title="Background" action={pencil('background')}>
        {shorthandPopover('background')}
        <div className="flex flex-col gap-0.5">
          {BG_ROWS.map((r) => propRow(r, 'background'))}
          {(effective('background-image', 'backgroundImage') || 'none') !== 'none' &&
            BG_IMAGE_ROWS.map((r) => propRow(r, 'background'))}
          {ownedExtras(/^background-|^-webkit-/, [...BG_ROWS, ...BG_IMAGE_ROWS]).map((p) => propRow({ prop: p }, 'background'))}
        </div>
      </Section>

      <Section title="Padding" action={pencil('padding')}>
        {shorthandPopover('padding', composeSides(s, 'padding'))}
        <SidesGrid prefix="padding" resolve={effective} onStyle={setProp} />
      </Section>

      <Section title="Margin" action={pencil('margin')}>
        {shorthandPopover('margin', composeSides(s, 'margin'))}
        <SidesGrid prefix="margin" resolve={effective} onStyle={setProp} />
      </Section>

      <Section title="Border" action={pencil('border')}>
        {shorthandPopover('border')}
        <div className="flex flex-col gap-0.5">
          {BORDER_ROWS.map((r) => propRow(r, 'border'))}
          {ownedExtras(/^border-/, BORDER_ROWS).map((p) => propRow({ prop: p }, 'border'))}
        </div>
      </Section>

      <BoxShadowSection
        value={effective('box-shadow', 'boxShadow')}
        onCommit={(v) => setProp('box-shadow', v === 'none' ? '' : v)}
        action={pencil('box-shadow')}
        popover={rawOpen['box-shadow'] && (
          <ShorthandPopover
            prop="box-shadow"
            seed={effective('box-shadow', 'boxShadow') === 'none' ? '' : effective('box-shadow', 'boxShadow')}
            onApply={(v) => setProp('box-shadow', v)}
            onClose={() => closePencil('box-shadow')}
          />
        )}
      />

      <TransformSection
        value={effective('transform', 'transform')}
        onCommit={(v) => setProp('transform', v === 'none' ? '' : v)}
        action={pencil('transform')}
        popover={rawOpen['transform'] && (
          <ShorthandPopover
            prop="transform"
            seed={effective('transform', 'transform') === 'none' ? '' : effective('transform', 'transform')}
            onApply={(v) => setProp('transform', v)}
            onClose={() => closePencil('transform')}
          />
        )}
      />

      <Section title="Interactive">
        <div className="flex gap-1">
          <LayoutSelect label="Cursor" initial={s.cursor || 'auto'} options={CURSORS} onCommit={(v) => setProp('cursor', v)} />
          <LayoutSelect label="Pointer events" initial={s.pointerEvents || 'auto'} options={POINTER_EVENTS} onCommit={(v) => setProp('pointer-events', v)} />
        </div>
      </Section>

      <MoreStylesSection styles={s} live={live} setProp={setProp} setPropBatch={setPropBatch} />
      {/* Bottom breathing room so the last rows / dropdowns / value overlays
          aren't flush against the panel's edge when scrolled to the end. */}
      <div className="h-32 shrink-0" />
    </div>
  )
}
